import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { Link } from "@/components/Link/Link";
import { OrderStatusSection } from "@/features/bookings/components/OrderStatusSection";
import type {
  FindUserReservationFlightReservation,
  FindUserReservationHotelReservation,
} from "@/types/codegen";
import { WHATSAPP_URL } from "@/utils/constants";
import { formatCurrency } from "@/utils/currency";

type Props = {
  reservation:
    | FindUserReservationFlightReservation
    | FindUserReservationHotelReservation;
};

export const OrderDetailsWithoutReservation = ({ reservation }: Props) => {
  const intl = useIntl();

  return (
    <>
      <OrderStatusSection status={reservation.status} />
      <section>
        <ul className="section-card w-full items-center justify-between gap-4 space-y-2 text-sm">
          <li className="flex w-full items-center justify-between">
            <span className="text-text-title">
              {intl.formatMessage({
                defaultMessage: "Order ID",
                description: "Label for the order ID in booking details",
              })}
              :
            </span>
            <span className="font-semibold text-text-title">
              {reservation.id}
            </span>
          </li>
          <li className="flex w-full items-center justify-between">
            <span className="text-text-title">
              {intl.formatMessage({
                defaultMessage: "Booking time",
                description: "Label for the exact time the booking was created",
              })}
              :
            </span>
            <span className="font-semibold text-text-title">
              {dayjs(reservation.createdAt).format("HH:mm, DD MMM YYYY")}
            </span>
          </li>
          <li className="flex w-full items-center justify-between">
            <span className="text-text-title">
              {intl.formatMessage({
                defaultMessage: "Total price",
                description: "Label for the total booking price",
              })}
              :
            </span>
            <span className="font-semibold text-text-title">
              {formatCurrency(reservation.price, reservation.currency)}
            </span>
          </li>
        </ul>
      </section>
      <section className="section-card mt-4 flex w-full flex-col items-start gap-1 space-y-2">
        <h3 className="mb-2 text-sm font-semibold">
          {intl.formatMessage({
            defaultMessage: "Do you need more information",
            description:
              "Prompt asking if the user needs additional information about their booking",
          })}
        </h3>
        <p className="text-sm text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Feel free to reach our support team via Whatsapp",
            description:
              "Button or link to contact the support team about a booking issue",
          })}
        </p>
        <div className="flex flex-col py-2">
          <Link to="." underline="hover" href={WHATSAPP_URL} target="_blank">
            {intl.formatMessage({
              defaultMessage: "Contact Support in WhatsApp",
              description:
                "Button to open WhatsApp to contact customer support",
            })}
          </Link>
        </div>
      </section>
    </>
  );
};
